<script setup lang="ts">
import { defineAsyncComponent, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { DOMAINS } from '../../lib/organization_assessment/domains'
import { DOMAIN_COUNT, QUESTIONS_PER_DOMAIN, judge } from '../../lib/organization_assessment/judge'
import { scrollModalToTop } from '../../lib/organization_assessment/scroll'
import { createEmptyAnswers } from '../../lib/organization_assessment/types'
import type { Answers, JudgeResult } from '../../lib/organization_assessment/types'

const OrganizationAssessmentIntro = defineAsyncComponent(() => import('./OrganizationAssessmentIntro.vue'))
const OrganizationAssessmentWizard = defineAsyncComponent(() => import('./OrganizationAssessmentWizard.vue'))
const OrganizationAssessmentLeadForm = defineAsyncComponent(() => import('./OrganizationAssessmentLeadForm.vue'))

const props = defineProps<{
  inflowRoute: string
}>()

type Screen = 'closed' | 'intro' | 'wizard' | 'leadForm'

const screen = ref<Screen>('closed')
const answers = reactive<Answers>(createEmptyAnswers(DOMAIN_COUNT, QUESTIONS_PER_DOMAIN))
const result = ref<JudgeResult | null>(null)

function open() {
  screen.value = 'intro'
}

function close() {
  screen.value = 'closed'
  Object.assign(answers, createEmptyAnswers(DOMAIN_COUNT, QUESTIONS_PER_DOMAIN))
  result.value = null
}

function startWizard() {
  screen.value = 'wizard'
}

function backToIntro() {
  screen.value = 'intro'
}

// 30問の回答が揃った時点でクライアント側で判定し、そのまま連絡先入力画面へ進む。
function completeWizard() {
  result.value = judge(answers)
  screen.value = 'leadForm'
}

// 開いている間はLP側のフローティングCTA（モックの .float 相当）を隠す。
// 画面が切り替わるたびに、モックの showScreen() と同様にスクロール位置を先頭へ戻す。
watch(screen, async (value) => {
  document.body.classList.toggle('organization_assessment_open', value !== 'closed')
  await nextTick()
  scrollModalToTop()
})

onMounted(() => {
  document.addEventListener('organization-assessment:open', open)
})

onUnmounted(() => {
  document.removeEventListener('organization-assessment:open', open)
  document.body.classList.remove('organization_assessment_open')
})
</script>

<template>
  <div v-if="screen !== 'closed'" class="organization_assessment_overlay">
    <div class="organization_assessment_modal">
      <button type="button" class="organization_assessment_close" aria-label="閉じる" @click="close">×</button>

      <OrganizationAssessmentIntro v-if="screen === 'intro'" :domains="DOMAINS" @start="startWizard" />

      <OrganizationAssessmentWizard
        v-else-if="screen === 'wizard'"
        :domains="DOMAINS"
        :answers="answers"
        @complete="completeWizard"
        @exit="backToIntro"
      />

      <OrganizationAssessmentLeadForm
        v-else-if="screen === 'leadForm' && result"
        :result="result"
        :answers="answers"
        :inflow-route="props.inflowRoute"
      />
    </div>
  </div>
</template>
